// apps/web/src/features/agent/AgentStepProgress.tsx
//
// Streamed step-progress indicator. The agent emits one `step` event at a
// time ({label, status}); this renders the current step as a small live
// status line so the user sees the run progressing.
import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/index.js';
import type { AgentStep } from './useAgentChat.js';

export interface AgentStepProgressProps {
  step: AgentStep;
}

export function AgentStepProgress({ step }: AgentStepProgressProps) {
  const { t } = useTranslation('agent');
  const done = step.status === 'done';
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="agent-step-progress"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: 'var(--s-2) var(--s-3)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-2)',
        color: 'var(--fg-2)',
        fontSize: 12,
      }}
    >
      <span
        className={done ? undefined : 'num'}
        style={{
          display: 'inline-flex',
          color: done ? 'var(--status-success-fg)' : 'var(--accent-1)',
          animation: done ? undefined : 'magimp-spin 1.2s linear infinite',
        }}
      >
        <Icon name={done ? 'check-square' : 'loader'} size={14} />
      </span>
      <span>{step.label}</span>
      <span style={{ color: 'var(--fg-3)' }}>
        {done ? t('progress.done') : t('progress.running')}
      </span>
      <style>{`@keyframes magimp-spin { from { transform: rotate(0) } to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}
